/**
 * Lightweight product funnel analytics (Adoption Spine growth).
 * Fire-and-forget; never blocks UX.
 */
import { API_BASE } from '../config/api';
import { getSessionToken, ensureGuestToken } from '../utils/sessionManager';

const SESSION_ANALYTICS_KEY = 'geovera_analytics_session';

export type AnalyticsEvent =
  | 'home_view'
  | 'map_open'
  | 'pricing_view'
  | 'lead_submit'
  | 'share_view'
  | 'signup_prompt_show'
  | 'signup_click'
  | 'account_view'
  | 'guest_claim';

function analyticsSessionKey(): string {
  try {
    let k = sessionStorage.getItem(SESSION_ANALYTICS_KEY);
    if (!k) {
      k = `as_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_ANALYTICS_KEY, k);
    }
    return k;
  } catch {
    return ensureGuestToken();
  }
}

/** Dedupe same event+path within a tab session (reduces double StrictMode fires). */
const fired = new Set<string>();

export function trackEvent(event: AnalyticsEvent, meta?: Record<string, unknown>): void {
  try {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const dedupeKey = `${event}|${path}`;
    // Allow lead_submit / guest_claim / signup_click always
    const always = new Set(['lead_submit', 'guest_claim', 'signup_click']);
    if (!always.has(event)) {
      if (fired.has(dedupeKey)) return;
      fired.add(dedupeKey);
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    const token = getSessionToken();
    if (token && !token.startsWith('guest_')) {
      headers.Authorization = `Bearer ${token}`;
    }

    // keepalive so navigations don't drop the beacon
    void fetch(`${API_BASE}/events`, {
      method: 'POST',
      headers,
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({
        event,
        path,
        sessionKey: analyticsSessionKey(),
        meta: meta || {},
      }),
    }).catch(() => {
      /* ignore analytics failures */
    });
  } catch {
    /* ignore */
  }
}
